import React, { useEffect, useMemo, useState } from "react";
import {
  Dimensions,
  PixelRatio,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  Switch,
  ActivityIndicator,
  Modal, // For the anonymity toggle
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import Colours from "../../utils/Colours";
import TopHeaderComponent from "../aTopHeader/TopHeaderComponent";
import { useNavigation, useRoute } from "@react-navigation/native";
import { useContext } from "react";
import AppContext from "../../context/AppContext";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import axios from "../../api/axios";
import ErrorAlertComp from "../../alertComp/ErrorAlertComp";
import { useQuery } from "@tanstack/react-query";
import { Image } from "expo-image";

const { width, height } = Dimensions.get("window");

// --- RESPONSIVE TOOLKIT ---
const BASE_WIDTH = 375;
const BASE_HEIGHT = 812;

const dynamicFontSize = (size) => {
  const scaleFactor = Math.min(width / BASE_WIDTH, height / BASE_HEIGHT);
  return Math.round(PixelRatio.roundToNearestPixel(size * scaleFactor));
};

const dynamicSpacing = (size) => (width / BASE_WIDTH) * size;
const dynamicSize = (size) => (width / BASE_WIDTH) * size;

const AnonymousOnboarding = () => {
  const [isErrorCompVisible, setIsErrorCompVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // const [minSpendOptions, setMinSpendOptions] = useState([]);
  // const [isLoading, setIsLoading] = useState(true);
  const [isLoading2, setIsLoading2] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [tableName, setTableName] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [isCustomSelected, setIsCustomSelected] = useState(false);

  const navigation = useNavigation();
  const route = useRoute();
  const { userToken } = useContext(AppContext);
  const axiosPrivate = useAxiosPrivate();
  const isLoggedIn = !!userToken; // Check if user has a token

  // 1. Grab the params from the URL
  //const { brandID, waiterID } = route.params || {};

  // const brandID = route?.params?.brandID;
  // const waiterID = route?.params?.waiterID;
  const qrToken = route?.params?.qrToken;
  /* ----------------------------------------------------
   * QR CHECK-IN HOOK: Fetch location details from server
   * ---------------------------------------------------- */
  const fetchQRDetails = async (token) => {
    if (!token) return null;
    // Server decodes the static QR token and returns the business details
    const response = await axios.get(`/getVerifyQR/getVerifyQR/${token}`);
    //console.log("Network Payload Check:", response.data); // Verify exact text block here
    return response.data; // Shape: { brandID: "...", waiterID: "...", brandName: "..." }
  };

  const {
    data: qrLocation,
    isLoading: isLoadingQR,
    isError: isQRError,
  } = useQuery({
    queryKey: ["verifyQR", qrToken],
    queryFn: () => fetchQRDetails(qrToken),
    enabled: !!qrToken,
    staleTime: 1000 * 60 * 60, // Static QR data can safely stay fresh for an hour
  });

  //console.log("qrLocationqrLocation", qrLocation);

  // 2. Extract our verified IDs safely returned from the backend response
  const brandID = qrLocation?.brandID;
  const waiterID = qrLocation?.waiterID;

  //console.log("brandID", brandID);
  //console.log("waiterID", waiterID);

  // 1. Define an isolated, pure data fetching function
  const fetchSpendTiers = async (brandID) => {
    if (!brandID) return [];
    const response = await axios.get(`/getSpendTier/getSpendTier/${brandID}`);
    return response.data;
  };

  // 2. Use the hook inside your functional component body
  const {
    data: minSpendOptions = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["spendTiers", brandID],
    queryFn: () => fetchSpendTiers(brandID),
    enabled: !!brandID, // Prevents running automatically if brandID is undefined/null
    staleTime: 1000 * 60 * 5, // Cache entries stay fresh for 5 minutes
  });

  useEffect(() => {
    if (isLoadingQR) return; // Wait for fetch to complete

    if (!brandID || !waiterID || brandID === "undefined" || waiterID === "undefined") {
      if (userToken) {
        navigation.replace("main");
      } else {
        navigation.navigate("getStarted");
      }
    }
  }, [brandID, waiterID, isLoadingQR]);

  useEffect(() => {
    //console.log("selectedAmmount", selectedAmount);
  }, [selectedAmount]);

  // Find the lowest tier price dynamically so the code adapts to any brand's rules
  const lowestAllowedSpend = useMemo(() => {
    if (!minSpendOptions || minSpendOptions.length === 0) return 0;
    return Math.min(...minSpendOptions.map((option) => Number(option.actualPrice)));
  }, [minSpendOptions]);

  // Check if the custom entry is lower than our baseline threshold
  const parsedCustomAmount = Number(customAmount.replace(/[^0-9]/g, "")); // strip non-numeric characters
  const isCustomAmountInvalid =
    isCustomSelected && parsedCustomAmount > 0 && parsedCustomAmount < lowestAllowedSpend;

  const canProceed = useMemo(() => {
    // 1. Validate the amount configuration first
    let isAmountValid = false;

    if (selectedAmount) {
      // Valid if a predefined card is chosen
      isAmountValid = true;
    } else if (isCustomSelected) {
      // Valid if custom input is greater than or equal to the lowest allowed tier
      const parsedAmount = Number(customAmount.replace(/[^0-9]/g, ""));
      isAmountValid = parsedAmount >= lowestAllowedSpend;
    }

    // 2. Apply your existing requirements on top of the amount validation
    if (isLoggedIn) {
      return !!(tableName && isAmountValid);
    } else {
      return !!(tableName && isAmountValid && whatsappNumber?.length > 10);
    }
  }, [
    isLoggedIn,
    tableName,
    selectedAmount,
    isCustomSelected,
    customAmount,
    lowestAllowedSpend,
    whatsappNumber,
  ]);

  // Handle selecting a predefined card
  const handleSelectTier = (id) => {
    setSelectedAmount(id);
    setIsCustomSelected(false);
    setCustomAmount(""); // Clear custom entry when picking a specific card
  };

  // Handle picking the custom field focus
  const handleSelectCustom = () => {
    setSelectedAmount(null);
    setIsCustomSelected(true);
  };

  const handleBackNavigation = () => {
    // const isLoggedIn = !!userToken; // Check if user has a token
    const isWeb = Platform.OS === "web";

    if (isWeb && !isLoggedIn) {
      // If on Web and NOT logged in, go to the public Company Home
      // Use navigation.navigate or window.location for a hard reset
      navigation.navigate("getStarted");
    } else {
      // Otherwise, just go back normally
      navigation.navigate("main");
    }
  };

  // useEffect(() => {
  //   const fetchTiers = async () => {
  //     try {
  //       setIsLoading(true);
  //       // Using the route we created in the backend
  //       const response = await axios.get(`/getSpendTier/getSpendTier/${brandID}`);
  //       setMinSpendOptions(response.data);
  //     } catch (err) {
  //       console.error("Error fetching tiers:", err);
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   };

  //   if (brandID) fetchTiers();
  // }, [brandID]);

  // const handleGetOTP = () => {
  //   //console.log("OTP Requested for:", tableName);
  //   navigation.navigate("anonymousOnboardingOTP");
  // };

  const handleProceed = async () => {
    let finalPhoneNumber;

    if (!isLoggedIn) {
      //console.log("Cleanedxx", whatsappNumber);

      // 1. Remove everything that isn't a number (removes spaces, +, dashes)
      let cleaned = whatsappNumber.replace(/\D/g, "");

      // 2. Remove ANY number of leading zeros (0, 00, 000...)
      cleaned = cleaned.replace(/^0+/, "");

      // 3. Remove leading 234 if it's now at the front
      if (cleaned.startsWith("234")) {
        cleaned = cleaned.slice(3);
      }

      // 4. Remove leading zero AGAIN (in case it was 234081...)
      cleaned = cleaned.replace(/^0+/, "");

      //console.log("Cleaned", cleaned);

      // Validation check
      if (cleaned.length !== 10) {
        // Show error...
        //console.log("number error");
        setErrorMessage("You might have entered a wrong number");
        setIsErrorCompVisible(true);
        return;
      }

      // 3. Final standardized format
      finalPhoneNumber = `234${cleaned}`;

      //console.log("Input:", whatsappNumber);
      //console.log("Cleaned (local):", cleaned);
      //console.log("Final (International):", finalPhoneNumber);
    }

    //console.log("NUMK", finalPhoneNumber);
    const payload = {
      phoneNumber: finalPhoneNumber, // The number the user typed
      tableName: tableName,
      spendTierID: selectedAmount,
      customSpend: customAmount,
      tableAnonymity: isAnonymous, // The true/false setting we discussed
      associatedBrand: brandID,
      waiterID,
    };
    //console.log("payload", payload);
    try {
      setIsLoading2(true);

      if (isLoggedIn) {
        //console.log("proceeding to payments");
        // 3. Make the actual API call
        const response = await axiosPrivate.post("/createVirtualTable/createVirtualTable", payload);

        if (response.data.success) {
          // PASS THE ID to the payment screen
          navigation.navigate("payForTable", {
            tableId: response.data.tableData._id,
            // tableData: response.data.tableData, // Pass the full data for instant loading..
          });
        }
      } else {
        // 2. Call your backend
        const response = await axios.post("/requestOtp/requestOtp", payload);

        if (response.status === 200) {
          // 3. Navigate and PASS the phone as a parameter
          navigation.navigate("anonymousOnboardingOTP", {
            whatsappNumber: payload.phoneNumber,
          });
        }
      }
    } catch (error) {
      console.error("Something went wrong", error);
      setErrorMessage("Something went wrong. Please try again");
      setIsErrorCompVisible(true);
    } finally {
      setIsLoading2(false);
    }
  };

  if (isLoadingQR) {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator size="large" color={Colours.PRIMARY} />
        <Text>Loading...</Text>
      </View>
    );
  }

  // // Don't render anything until params are confirmed
  // if (!brandID || !waiterID || brandID === "undefined" || waiterID === "undefined") {
  //   return null;
  // }

  if (!brandID || !waiterID || brandID === "undefined" || waiterID === "undefined") {
    return null;
  }

  // ✅ Don't redirect while QR is still being fetched

  return (
    <SafeAreaView style={styles.safeArea}>
      <TopHeaderComponent headerText={"Join the Show"} hideScreen={() => handleBackNavigation()} />

      {/* <View style={styles.brandHeroCard}>
        <Image
          source={{ uri: qrLocation?.brandImage || "https://placehold.co/150" }}
          style={styles.brandImage}
          contentFit="cover"
          cachePolicy="disk"
          // onError={handleImageError}
        />
        <View style={styles.brandMetaInfo}>
          <Text style={styles.nameTxt} numberOfLines={1}>
            {qrLocation?.brandName || "Company Title"}
          </Text>
          <Text style={styles.emailTxt} numberOfLines={2}>
            {qrLocation?.brandAddress || "Location Details Not Set"}
          </Text>
        </View>
      </View> */}

      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={{ flex: 1 }}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.brandHeroCard}>
            <Image
              source={{ uri: qrLocation?.brandImage || "https://placehold.co/150" }}
              style={styles.brandImage}
              contentFit="cover"
              cachePolicy="disk"
              // onError={handleImageError}
            />
            <View style={styles.brandMetaInfo}>
              <Text style={styles.nameTxt} numberOfLines={1}>
                {qrLocation?.brandName || "Company Title"}
              </Text>
              <Text style={styles.emailTxt} numberOfLines={2}>
                {qrLocation?.brandAddress || "Location Details Not Set"}
              </Text>
            </View>
          </View>
          {/* STEP 1: VIRTUAL TABLE NAME */}
          <View style={styles.section}>
            <Text style={styles.sectionLabel}>1. CREATE YOUR VIRTUAL TABLE</Text>
            <View style={styles.inputWrapper}>
              <MaterialCommunityIcons
                name="pencil-outline"
                size={dynamicSize(20)}
                color={Colours.PRIMARY}
              />
              <TextInput
                style={styles.input}
                placeholder="Give your table a name (e.g. OBO Table)"
                value={tableName}
                onChangeText={setTableName}
                placeholderTextColor="#999"
              />
            </View>

            {/* ANONYMOUS CHECKBOX/TOGGLE */}
            <TouchableOpacity
              style={styles.toggleRow}
              activeOpacity={0.7}
              onPress={() => setIsAnonymous(!isAnonymous)}
            >
              <View style={styles.toggleTextContent}>
                <Text style={styles.toggleTitle}>Stay Anonymous</Text>
                <Text style={styles.toggleSubTitle}>Hide your name from other guests</Text>
              </View>
              <Switch
                trackColor={{ false: "#767577", true: Colours.PRIMARY }}
                thumbColor={isAnonymous ? "#fff" : "#f4f3f4"}
                onValueChange={() => setIsAnonymous(!isAnonymous)}
                value={isAnonymous}
              />
            </TouchableOpacity>
          </View>

          {/* STEP 2: MINIMUM SPEND SELECTION */}
          {/* <View style={styles.section}>
            <Text style={styles.sectionLabel}>2. SELECT MINIMUM SPEND</Text>

            {isLoading ? (
              <View style={styles.loaderContainer}>
                <ActivityIndicator size="large" color={Colours.PRIMARY} />
                <Text style={styles.loadingText}>Fetching available tiers...</Text>
              </View>
            ) : minSpendOptions.length > 0 ? (
              minSpendOptions.map((item) => (
                <TouchableOpacity
                  key={item._id} // Using MongoDB _id
                  style={[styles.amountCard, selectedAmount === item._id && styles.activeCard]}
                  onPress={() => setSelectedAmount(item._id)}
                >
                  <View style={{ flex: 1 }}>
                    <Text
                      style={[styles.cardLabel, selectedAmount === item._id && { color: "white" }]}
                    >
                      {item.caption}
                    </Text>
                    <Text
                      style={[
                        styles.cardSubLabel,
                        selectedAmount === item._id && { color: "rgba(255,255,255,0.7)" },
                      ]}
                    >
                      Up to {item.maxUsers} Guests • {item.description}
                    </Text>
                  </View>
                  <Text
                    style={[styles.amountText, selectedAmount === item._id && { color: "white" }]}
                  >
                    ₦{Number(item.actualPrice).toLocaleString()}
                  </Text>
                </TouchableOpacity>
              ))
            ) : (
              <Text style={styles.emptyText}>No minimum spend tiers available for this brand.</Text>
            )}
          </View> */}

          {/* STEP 2: MINIMUM SPEND SELECTION */}
          <View style={styles.section}>
            <Text style={styles.sectionLabel}>2. SELECT MINIMUM SPEND</Text>

            {isLoading ? (
              <View style={styles.loaderContainer}>
                <ActivityIndicator size="large" color={Colours.PRIMARY} />
                <Text style={styles.loadingText}>Fetching available tiers...</Text>
              </View>
            ) : isError ? (
              <View style={styles.errorStateContainer}>
                <Text style={styles.emptyText}>Failed to load options. Please try again.</Text>
              </View>
            ) : minSpendOptions.length > 0 ? (
              <View>
                {/* Dynamic Tier List */}
                {minSpendOptions.map((item) => (
                  <TouchableOpacity
                    key={item._id}
                    style={[styles.amountCard, selectedAmount === item._id && styles.activeCard]}
                    onPress={() => handleSelectTier(item._id)}
                  >
                    <View style={{ flex: 1 }}>
                      <Text
                        style={[
                          styles.cardLabel,
                          selectedAmount === item._id && { color: "white" },
                        ]}
                      >
                        {item.caption}
                      </Text>
                      <Text
                        style={[
                          styles.cardSubLabel,
                          selectedAmount === item._id && { color: "rgba(255,255,255,0.7)" },
                        ]}
                      >
                        Up to {item.maxUsers} Guests • {item.description}
                      </Text>
                    </View>
                    <Text
                      style={[styles.amountText, selectedAmount === item._id && { color: "white" }]}
                    >
                      ₦{Number(item.actualPrice).toLocaleString()}
                    </Text>
                  </TouchableOpacity>
                ))}

                {/* --- CUSTOM AMOUNT OPTION --- */}
                {/* <TouchableOpacity
                  style={[styles.amountCard, isCustomSelected && styles.activeCard]}
                  onPress={handleSelectCustom}
                  activeOpacity={0.8}
                >
                  <View style={{ flex: 1, justifyContent: "center" }}>
                    <Text style={[styles.cardLabel, isCustomSelected && { color: "white" }]}>
                      Custom Minimum Spend
                    </Text>
                    <Text
                      style={[
                        styles.cardSubLabel,
                        isCustomSelected && { color: "rgba(255,255,255,0.7)" },
                      ]}
                    >
                      Enter your preferred budget threshold
                    </Text>
                  </View>

                  <View
                    style={[
                      styles.customInputContainer,
                      isCustomSelected && styles.activeInputContainer,
                    ]}
                  >
                    <Text style={[styles.currencyPrefix, isCustomSelected && { color: "white" }]}>
                      ₦
                    </Text>
                    <TextInput
                      style={[styles.customTextInput, isCustomSelected && { color: "white" }]}
                      placeholder="0"
                      placeholderTextColor={isCustomSelected ? "rgba(255,255,255,0.5)" : "#999"}
                      keyboardType="numeric"
                      value={customAmount}
                      onChangeText={(text) => setCustomAmount(text.replace(/[^0-9]/g, ""))}
                      onFocus={handleSelectCustom}
                    />
                  </View>
                </TouchableOpacity>

                {isCustomAmountInvalid && (
                  <Text style={styles.errorText}>
                    The minimum allowed budget option for this brand is ₦
                    {lowestAllowedSpend.toLocaleString()}.
                  </Text>
                  
                )} */}
              </View>
            ) : (
              <Text style={styles.emptyText}>No minimum spend tiers available for this brand.</Text>
            )}
            {/* Predefined cards map finishes here... */}

            {/* Elegant Separator */}
            <View style={styles.dividerRow}>
              <View style={styles.dividerLine} />
              {/* <Text style={styles.dividerText}>OR ENTER CUSTOM BUDGET</Text> */}
              <Text style={styles.dividerText}>OR ENTER CUSTOM SPEND</Text>

              <View style={styles.dividerLine} />
            </View>

            {/* Dedicated Input Field */}
            <View style={styles.inputSection}>
              <View
                style={[
                  styles.premiumInputContainer,
                  isCustomSelected && styles.inputFocused,
                  isCustomAmountInvalid && styles.inputError,
                ]}
              >
                <Text style={styles.inputCurrency}>₦</Text>
                <TextInput
                  style={styles.fieldInput}
                  placeholder={`Minimum ₦${lowestAllowedSpend.toLocaleString()}`}
                  placeholderTextColor="#A3A3A3"
                  keyboardType="numeric"
                  value={customAmount}
                  onChangeText={(text) => {
                    const cleaned = text.replace(/[^0-9]/g, "");
                    setCustomAmount(cleaned);
                    if (cleaned !== "") {
                      setIsCustomSelected(true);
                      setSelectedAmount(null); // Deselect predefined cards
                    } else {
                      setIsCustomSelected(false);
                    }
                  }}
                  onFocus={() => {
                    setIsCustomSelected(true);
                    setSelectedAmount(null);
                  }}
                />
              </View>

              {/* Minimalist Error Label */}
              {isCustomAmountInvalid && (
                <Text style={styles.cleanErrorText}>
                  Amount must be at least ₦{lowestAllowedSpend.toLocaleString()} for this venue.
                </Text>
              )}
            </View>
          </View>

          {/* WHATSAPP VERIFICATION */}
          {!isLoggedIn && (
            <View style={styles.section}>
              <Text style={styles.sectionLabel}>3. WHATSAPP FOR OTP</Text>
              <View style={styles.inputWrapper}>
                <MaterialCommunityIcons name="whatsapp" size={dynamicSize(24)} color="#25D366" />
                <TextInput
                  style={styles.input}
                  placeholder="WhatsApp Number"
                  keyboardType="phone-pad"
                  value={whatsappNumber}
                  onChangeText={setWhatsappNumber}
                  placeholderTextColor="#999"
                />
              </View>
            </View>
          )}

          {/* PROCEED BUTTON */}
          <TouchableOpacity
            style={[styles.proceedBtn, !canProceed && styles.disabledBtn]}
            onPress={() => {
              handleProceed();
            }}
            disabled={!canProceed || isLoading2}
          >
            {/* <Text style={styles.proceedBtnText}>Get OTP to Confirm</Text>
            <MaterialCommunityIcons name="arrow-right" size={dynamicSize(20)} color="white" /> */}
            {isLoading2 ? (
              <ActivityIndicator color="white" />
            ) : (
              <>
                <Text style={styles.proceedBtnText}>
                  {isLoggedIn ? "Proceed to Payment" : "Get OTP to Confirm"}
                </Text>
                <MaterialCommunityIcons
                  name={isLoggedIn ? "credit-card-outline" : "arrow-right"}
                  size={dynamicSize(20)}
                  color="white"
                />
              </>
            )}
          </TouchableOpacity>

          <Text style={styles.footerNote}>
            Paystack secure payment follows {isLoggedIn ? "checkout" : "verification"}.
          </Text>
        </ScrollView>
      </KeyboardAvoidingView>
      <Modal visible={isErrorCompVisible} transparent={true} animationType="fade">
        <ErrorAlertComp message={errorMessage} onClose={() => setIsErrorCompVisible(false)} />
      </Modal>
    </SafeAreaView>
  );
};

export default AnonymousOnboarding;

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: "white" },
  scrollContent: {
    padding: dynamicSpacing(20),
    paddingBottom: dynamicSpacing(150),
    backgroundColor: Colours.LIGHTEST,
  },
  section: { marginBottom: dynamicSpacing(25) },
  sectionLabel: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(12),
    color: "#888",
    letterSpacing: 1.2,
    marginBottom: dynamicSpacing(12),
  },

  brandHeroCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "white",
    padding: dynamicSpacing(20),
    borderRadius: dynamicSize(16),
    marginBottom: dynamicSpacing(25),
    elevation: 2,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
  },
  brandImage: {
    width: dynamicSize(70),
    height: dynamicSize(70),
    borderRadius: dynamicSize(14), // Modern soft-rounded corners instead of circles
    backgroundColor: "#F5F5F7",
  },
  brandMetaInfo: {
    flex: 1,
    marginLeft: dynamicSpacing(15),
    justifyContent: "center",
  },
  nameTxt: {
    color: Colours.DARKGRAY,
    fontSize: dynamicFontSize(18),
    fontFamily: "Sen-Bold",
    marginBottom: dynamicSpacing(4),
  },
  emailTxt: {
    color: Colours.DARKLIGHTGRAY,
    fontSize: dynamicFontSize(13),
    fontFamily: "Sen-Regular",
    lineHeight: dynamicFontSize(18),
  },

  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "white",
    paddingHorizontal: dynamicSpacing(15),
    borderRadius: dynamicSize(12),
    borderWidth: 1,
    borderColor: "#DDD",
    height: dynamicSize(56),
    marginBottom: dynamicSpacing(10),
  },
  input: {
    flex: 1,
    marginLeft: dynamicSpacing(10),
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(15),
    color: Colours.DARKGRAY,
  },
  toggleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#fff",
    padding: dynamicSpacing(12),
    borderRadius: dynamicSize(12),
    marginTop: dynamicSpacing(5),
    borderWidth: 1,
    borderColor: "#F0F0F0",
  },
  toggleTextContent: { flex: 1 },
  toggleTitle: { fontFamily: "Sen-Bold", fontSize: dynamicFontSize(14), color: Colours.DARKGRAY },
  toggleSubTitle: { fontFamily: "Sen-Regular", fontSize: dynamicFontSize(11), color: "gray" },

  amountCard: {
    backgroundColor: "white",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: dynamicSpacing(18),
    borderRadius: dynamicSize(16),
    marginBottom: dynamicSpacing(10),
    borderWidth: 1,
    borderColor: "#EEE",
    elevation: 3,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
  },
  activeCard: { backgroundColor: Colours.PRIMARY, borderColor: Colours.PRIMARY },
  cardLabel: { fontFamily: "Sen-Bold", fontSize: dynamicFontSize(15), color: Colours.DARKGRAY },
  cardSubLabel: { fontFamily: "Sen-Regular", fontSize: dynamicFontSize(12), color: "#999" },
  amountText: { fontFamily: "Sen-Bold", fontSize: dynamicFontSize(16), color: Colours.PRIMARY },

  proceedBtn: {
    backgroundColor: Colours.PRIMARY,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: dynamicSpacing(18),
    borderRadius: dynamicSize(15),
    gap: dynamicSpacing(10),
    marginTop: dynamicSpacing(10),
  },
  disabledBtn: { backgroundColor: "#CCC" },
  proceedBtnText: { color: "white", fontFamily: "Sen-Bold", fontSize: dynamicFontSize(16) },
  footerNote: {
    textAlign: "center",
    color: "#AAA",
    fontSize: dynamicFontSize(11),
    marginTop: dynamicSpacing(20),
  },
  /* Error & Empty State Layout Rules */
  errorStateContainer: {
    paddingVertical: dynamicSpacing(40),
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },

  emptyText: {
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Regular",
    color: Colours.DARKLIGHTGRAY, // Matches your platform's muted slate text
    textAlign: "center",
    lineHeight: dynamicFontSize(20),
    paddingHorizontal: dynamicSpacing(24),
  },
  dividerRow: {
    flexDirection: "row",
    alignItems: "center",
    marginVertical: dynamicSpacing(20),
    paddingHorizontal: dynamicSpacing(10),
  },
  dividerLine: {
    flex: 1,
    height: dynamicSize(1),
    backgroundColor: "#E5E5E5",
  },
  dividerText: {
    fontSize: dynamicFontSize(11),
    fontWeight: "600",
    color: "#8C8C8C",
    paddingHorizontal: dynamicSpacing(14),
    letterSpacing: 1, // Note: letterSpacing stringently accepts raw ratios across layout platforms, leave unscaled
  },

  // Input Section
  inputSection: {
    paddingHorizontal: dynamicSpacing(4),
  },
  premiumInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FAFAFA",
    borderWidth: dynamicSize(1.5),
    borderColor: "#EAEAEA",
    borderRadius: dynamicSize(12),
    paddingHorizontal: dynamicSpacing(16),
    height: dynamicSize(56),
  },
  inputFocused: {
    borderColor: "#1A1A1A",
    backgroundColor: "#FFFFFF",
  },
  inputError: {
    borderColor: "#FF3B30",
    backgroundColor: "#FFF9F9",
  },

  // Typography
  inputCurrency: {
    fontSize: dynamicFontSize(18),
    fontWeight: "600",
    color: "#1A1A1A",
    marginRight: dynamicSpacing(6),
  },
  fieldInput: {
    flex: 1,
    fontSize: dynamicFontSize(16),
    fontWeight: "600",
    color: "#1A1A1A",
    padding: 0,
  },
  cleanErrorText: {
    color: "#FF3B30",
    fontSize: dynamicFontSize(12),
    fontWeight: "500",
    marginTop: dynamicSpacing(6),
    paddingLeft: dynamicSpacing(4),
  },
});
